'use client'
import React, {useEffect, useState} from 'react'
import DateSVG from '@local/assets/svgs/icon-date.svg'
import GlobeSVG from '@local/assets/svgs/icon-globe.svg'
import BedSVG from '@local/assets/svgs/icon-bed.svg'
import HourGlassSVG from '@local/assets/svgs/icon-hourglass.svg'
import InfoSVG from '@local/assets/svgs/icon-info.svg'
import {cx} from '@local/utils/cx'

type SummaryProps = {
	location: string;
	duration: string;
	suits: number;
	program: string;
	rating: string;
	idealPeriod: string;
	hotelDescription: string;
	amenities: string[];
};

export const Summary = ({
	location,
	duration,
	suits,
	program,
	rating,
	idealPeriod,
	hotelDescription,
	amenities,
}: SummaryProps) => {
	const [expanded, setExpanded] = useState(false)
	const [isMobile, setIsMobile] = useState(false)

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 1024) // lg breakpoint
		}
		checkMobile()
		window.addEventListener('resize', checkMobile)
		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	return (
		<div className='gap-24 lg:gap-40 flex flex-col' id='summary'>

			<h2 className='text-28 lg:text-34 font-serif font-medium leading-tight'>Summary</h2>

			{/* Info Grid */}
			<div className='grid grid-cols-1 sm:grid-cols-2 gap-x-21 pr-4'>
				{location && (
					<InfoRow
						icon={<GlobeSVG className='w-16 h-16' />}
						label='LOCATION'
						value={location} />
				)}
				{duration && (
					<InfoRow
						icon={<HourGlassSVG className='w-16 h-16' />}
						label='DURATION'
						value={duration} />
				)}
				{suits && (
					<InfoRow
						icon={<BedSVG className='w-16 h-16' />}
						label='SUITS'
						value={`${suits}`} />
				)}
				{rating && (
					<InfoRow
						icon={<p className='text-16 text-black font-medium'>☆</p>}
						label='RATING'
						value={`${rating} ☆`} />
				)}
				{program && (
					<InfoRow
						icon={<InfoSVG className='w-16 h-16' />}
						label='PROGRAM'
						value={program} />
				)}
				{idealPeriod && (
					<InfoRow
						icon={<DateSVG className='w-16 h-16' />}
						label='IDEAL PERIOD'
						value={idealPeriod} />
				)}
			</div>

			{/* Hotel Description */}
			<div>
				<h3 className='text-18 leading-moderate font-[350] mb-10'>
					Few words about hotel
				</h3>
				<ExpandableContent text={hotelDescription} limit={isMobile ? 160 : 400} />
			</div>

			{/* Amenities */}
			<div className='mt-6'>
				<h3 className='text-18 leading-moderate font-[350] mb-10'>
					What you will have access to:
				</h3>
				<ul className='list-disc text-grey-2 list-inside text-14 leading-normal font-[350]'>
					{amenities.map((item, index) => (
						<li key={index}>{item}</li>
					))}
				</ul>
			</div>
		</div>
	)
}

// Helper component for Info Rows
const InfoRow = ({
	icon,
	label,
	value,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
}) => (
	<div className='flex flex-row items-center justify-between border-t border-grey-1 py-7 '>
		<div className='flex flex-row  items-center gap-x-10 flex-1'>
			<span className='flex items-center justify-center'>{icon}</span>
			<p className='text-12 text-black font-medium'>{label}</p>
		</div>
		<span className='text-12 pl-20 text-grey-2 leading-none  uppercase font-medium flex-1 text-left'>{value}</span>
	</div>
)

type ExpandableContentProps = {
	text: string;
	limit: number;
	textClassName?: string;
	containerClassName?: string;
};

const ExpandableContent = ({
	text,
	limit = 120,
	textClassName,
	containerClassName,
}: ExpandableContentProps) => {
	const [expanded, setExpanded] = useState(false)

	const truncatedText = text.substring(0, limit) + '...'

	return (
		<div className={cx('flex flex-col gap-y-10', containerClassName)}>
			<p
				className={cx(
					'text-14 text-grey-2 leading-normal font-[350]',
					textClassName,
				)}>
				{expanded ? text : truncatedText}
			</p>
			{text.length > limit && (
				<button
					onClick={() => setExpanded(!expanded)}
					className='text-12 text-black font-medium underline leading-none self-start'>
					{expanded ? 'SHOW LESS' : 'READ MORE'}
				</button>
			)}
		</div>
	)
}
